import { useAtom } from 'jotai';
import { persistedOverviewAtom } from '../atoms';
import { useMemo } from 'react';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { getDimensionData } from '@api/dimensionContainer';
import instance from '@api/axiosInstance';
import { trackQueryError } from '@utils/sensor/apptracker';
import useQueryParams from './useQueryParams';
import { DATASET } from '@utils/constants';

/**
 * Hook to fetch and manage engagement event data
 * Fetches top 500 events using group-by query with AppTotalEvents metric
 */
function useEventsOverview() {
  const [{ events }] = useAtom(persistedOverviewAtom);
  const { interval, filter, _rKey } = useQueryParams();
  const EVENT_DIMENSION = 'event_name';
  const metric = 'AppTotalEvents';

  // Build group-by query to fetch top 500 events
  const groupByQuery = useMemo(() => {
    if (!interval.from || !interval.to) {
      return null;
    }

    return {
      type: 'group-by',
      dataset: DATASET,
      metrics: [metric],
      interval: `${moment(interval.from).toISOString()}/${moment(interval.to).toISOString()}`,
      granularity: 'ALL',
      filter: filter || [],
      limit: 500,
      groupBy: [EVENT_DIMENSION],
      sortBy: [{ metric, order: 'desc' }],
      options: {
        withTotals: true,
      },
    };
  }, [metric, filter, interval, _rKey]);

  // Fetch events data using react-query
  const {
    data = { data: [], totals: {} },
    refetch,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['dpi-overview', 'events', groupByQuery],
    queryFn: () => getDimensionData({ queries: [groupByQuery] }, {}, instance),
    enabled: !!groupByQuery,
    refetchOnWindowFocus: false,
    select: (response) => {
      const { data = [], totals = {} } = get(response, ['data', 0], {});
      return { data, totals };
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (err) => {
      trackQueryError({ type: 'dpi_overview_events', err });
    },
  });

  // Process event list from API response
  const { eventList = [], validEvents = [] } = useMemo(() => {
    const eventRows = get(data, ['data', '0', 'results'], []);
    if (eventRows.length > 0) {
      const validEvents = eventRows.filter(
        (eachEvent) =>
          !['UNKNOWN'].includes(eachEvent[EVENT_DIMENSION]) && !isNaN(eachEvent[metric]),
      );
      const eventList = validEvents.map((event) => event[EVENT_DIMENSION]);
      return { eventList, validEvents };
    }
    return { eventList: [], validEvents: [] };
  }, [data, metric]);

  // Get ordered event IDs from persisted state
  const orderedEventIds = events
    ? Object.entries(events)
        .sort(([, a], [, b]) => a.order - b.order)
        .map(([id]) => id)
    : [];

  return {
    name: 'overview-event-store',
    events: orderedEventIds,
    eventsData: events || {},
    eventList,
    validEvents,
    isLoading: isFetching,
    isError,
    refetch,
    totals: data.totals || {},
  };
}

export default useEventsOverview;
